import { Modal, Button, Typography, Input, Space, notification } from 'antd';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { deleteAllCustomProducts } from '../../api/deleteAllCustomProducts';
import { fetchCustomProducts } from '../../store/customProductSlice';
import { CustomProductBulkTarget } from '../../types/customProductBulkActions';

interface UploadCustomProductModalProps {
    open: boolean;
    onClose: () => void;
    bulkTarget: CustomProductBulkTarget;
    selectedCount: number;
    hasActiveFilters: boolean;
}

const DeleteCustomProductsModal: React.FC<UploadCustomProductModalProps> = ({
    open,
    onClose,
    bulkTarget,
    selectedCount,
    hasActiveFilters,
}) => {
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState(false);
    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
    const [confirmation, setConfirmation] = useState('');


    const handleDeleteCustomProducts = async () => {
        setLoading(true);
        try {
            if (!selectedCompany) return;
            const result = await deleteAllCustomProducts(selectedCompany?.id, bulkTarget);
            dispatch(fetchCustomProducts({
                companyId: selectedCompany.id,
                pageSize: 10,
                lastVisibleDoc: null,
                activeSorter: null,
                activeFilters: {},
                refetch: true
            }))
            notification.success({
                message: 'Products deleted',
                description: `Deleted ${result.deletedCount ?? result.requestedCount ?? ''} custom products.`,
            });
            handleClose();
        } catch (error) {
            console.error(error);
            notification.error({
                message: 'Error deleting Products',
                description: 'Failed to delete custom products. Please try again later.',
            })
        } finally {
            setLoading(false);
        }
    };

    const scopeLabel = (() => {
        if (bulkTarget.scope === 'selected') {
            return `${selectedCount} selected custom products`;
        }
        if (bulkTarget.scope === 'filtered') {
            return 'all custom products matching the current filters';
        }
        return 'all custom products for this company';
    })();

    const confirmationRequired = confirmation !== selectedCompany?.name;

    const handleClose = () => {
        setConfirmation('');
        onClose();
    };

    return (
        <Modal
            open={open}
            title={`Delete ${scopeLabel}`}
            onCancel={handleClose}
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="delete"
                    danger
                    type="primary"
                    onClick={handleDeleteCustomProducts}
                    loading={loading}
                    disabled={confirmationRequired}
                >
                    Delete
                </Button>,
            ]}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Typography.Paragraph>
                    Are you sure you want to <strong>permanently delete</strong> {scopeLabel}? This action <strong>cannot be undone</strong>.
                </Typography.Paragraph>

                {hasActiveFilters && bulkTarget.scope === 'selected' ? (
                    <Typography.Text type="secondary">
                        Current table filters will be ignored because selected rows take priority.
                    </Typography.Text>
                ) : null}

                <Typography.Text type="danger">
                    Please type <strong>{selectedCompany?.name}</strong> to confirm.
                </Typography.Text>

                <Input
                    placeholder={selectedCompany?.name}
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                />
            </Space>
        </Modal>
    );
};

export default DeleteCustomProductsModal;
